import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
const fmtK = n => { const v=Number(n||0); return v>=1000000?'\u20a6'+(v/1000000).toFixed(1)+'M':v>=1000?'\u20a6'+(v/1000).toFixed(0)+'K':'\u20a6'+v.toLocaleString(); };

export default function JobCostingReport() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [to, setTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState('date');
  const [filterStatus, setFilterStatus] = useState('');

  if (!['super_admin','admin'].includes(user?.role)) {
    return <div className="empty-state"><h3>Access Restricted</h3><p>Job Costing Report is only available to Admin and Super Admin.</p></div>;
  }

  const load = () => {
    setLoading(true);
    get('/job-costing/report?from='+from+'&to='+to)
      .then(d => setData(d.data||[]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = data
    .filter(j => !filterStatus || j.status === filterStatus)
    .sort((a,b) => {
      if (sortBy==='profit') return b.gross_profit - a.gross_profit;
      if (sortBy==='margin') return parseFloat(b.margin) - parseFloat(a.margin);
      if (sortBy==='revenue') return b.revenue - a.revenue;
      return new Date(b.date) - new Date(a.date);
    });

  const totals = filtered.reduce((acc, j) => ({
    revenue: acc.revenue + j.revenue,
    material_cost: acc.material_cost + j.material_cost,
    external_cost: acc.external_cost + j.external_cost,
    total_cost: acc.total_cost + j.total_cost,
    gross_profit: acc.gross_profit + j.gross_profit,
  }), { revenue:0, material_cost:0, external_cost:0, total_cost:0, gross_profit:0 });

  const avgMargin = filtered.length > 0
    ? (filtered.reduce((s,j)=>s+parseFloat(j.margin||0),0) / filtered.length).toFixed(1)
    : 0;

  // Chart data - top 10 by revenue
  const chartData = [...filtered]
    .sort((a,b)=>b.revenue-a.revenue)
    .slice(0,10)
    .map(j => ({
      name: j.job_number,
      Revenue: j.revenue,
      Cost: j.total_cost,
      Profit: j.gross_profit
    }));

  const setQuickRange = (type) => {
    const d = new Date();
    if (type==='this_month') { setFrom(new Date(d.getFullYear(),d.getMonth(),1).toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); }
    if (type==='last_month') { setFrom(new Date(d.getFullYear(),d.getMonth()-1,1).toISOString().split('T')[0]); setTo(new Date(d.getFullYear(),d.getMonth(),0).toISOString().split('T')[0]); }
    if (type==='this_year') { setFrom(d.getFullYear()+'-01-01'); setTo(d.toISOString().split('T')[0]); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Job Costing Report</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Revenue, costs and gross profit per job order</p>
        </div>
        <button className="btn btn-outline" onClick={()=>window.print()}>Print Report</button>
      </div>

      {/* Filters */}
      <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:20}}>
        <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">From</label>
            <input type="date" className="form-control" value={from} onChange={e=>setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">To</label>
            <input type="date" className="form-control" value={to} onChange={e=>setTo(e.target.value)} />
          </div>
          <div style={{display:'flex',gap:6}}>
            {[['This Month','this_month'],['Last Month','last_month'],['This Year','this_year']].map(([l,k])=>(
              <button key={k} className="btn btn-ghost btn-sm" onClick={()=>setQuickRange(k)}>{l}</button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading?'Loading...':'Run Report'}
          </button>
        </div>
      </div>

      {loading ? <div className="spinner"></div> : data.length === 0 ? (
        <div className="empty-state"><h3>No job orders found</h3><p>Try a different date range.</p></div>
      ) : (
        <>
          {/* Summary tiles */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12,marginBottom:20}}>
            {[
              { label:'Total Jobs', value:filtered.length, color:'#1E4FAD', isNum:false },
              { label:'Total Revenue', value:fmtK(totals.revenue), color:'#556B2F', isNum:false },
              { label:'Material Cost', value:fmtK(totals.material_cost), color:'#8B5E0A', isNum:false },
              { label:'External Cost', value:fmtK(totals.external_cost), color:'#8B5E0A', isNum:false },
              { label:'Gross Profit', value:fmtK(totals.gross_profit), color:totals.gross_profit>=0?'#2E6B3A':'#8F2E24', isNum:false },
              { label:'Avg Margin', value:avgMargin+'%', color:parseFloat(avgMargin)>=30?'#2E6B3A':parseFloat(avgMargin)>=15?'#8B5E0A':'#8F2E24', isNum:false },
            ].map(tile => (
              <div key={tile.label} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,padding:'14px 16px',boxShadow:'var(--shadow)'}}>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>{tile.label}</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:tile.color}}>{tile.value}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:20}}>
              <div style={{background:'#556B2F',padding:'12px 16px'}}>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
                  Top 10 Jobs — Revenue vs Cost vs Profit
                </span>
              </div>
              <div style={{padding:16}}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                    <Tooltip formatter={v=>fmt(v)} />
                    <Bar dataKey="Revenue" fill="#556B2F" radius={[3,3,0,0]} />
                    <Bar dataKey="Cost" fill="#8F2E24" radius={[3,3,0,0]} />
                    <Bar dataKey="Profit" fill="#A8893D" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Controls */}
          <div style={{display:'flex',gap:10,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
            <select className="form-control" style={{width:'auto',fontSize:12}}
              value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              <option value="date">Sort by Date</option>
              <option value="profit">Sort by Profit</option>
              <option value="margin">Sort by Margin %</option>
              <option value="revenue">Sort by Revenue</option>
            </select>
            <select className="form-control" style={{width:'auto',fontSize:12}}
              value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="dispatched">Dispatched</option>
              <option value="in_progress">In Progress</option>
            </select>
            <span style={{fontSize:12,color:'#8A8A8A'}}>{filtered.length} job{filtered.length!==1?'s':''}</span>
          </div>

          {/* Table */}
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#556B2F'}}>
                  {['Job No.','Date','Customer','Product','Qty','Revenue','Material Cost','External Cost','Total Cost','Gross Profit','Margin %','Status'].map(h=>(
                    <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((j,i) => (
                  <tr key={j.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#556B2F'}}>{j.job_number}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{j.date}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{j.customer_name||'—'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{j.product_description||'—'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{j.quantity||'—'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#556B2F',fontWeight:600}}>{fmt(j.revenue)}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#8F2E24'}}>{fmt(j.material_cost)}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#8F2E24'}}>{fmt(j.external_cost)}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#8F2E24',fontWeight:600}}>{fmt(j.total_cost)}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,fontWeight:700,color:j.gross_profit>=0?'#2E6B3A':'#8F2E24'}}>{fmt(j.gross_profit)}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:700,color:parseFloat(j.margin)>=30?'#2E6B3A':parseFloat(j.margin)>=15?'#8B5E0A':'#8F2E24'}}>{j.margin}%</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                        background:j.status==='completed'||j.status==='dispatched'?'#E8F5EB':j.status==='in_progress'?'#EDF4FF':'#F2EFE7',
                        color:j.status==='completed'||j.status==='dispatched'?'#2E6B3A':j.status==='in_progress'?'#1E4FAD':'#8A8A8A'}}>
                        {j.status?.replace('_',' ')}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{background:'#F2EFE7',fontWeight:700}}>
                  <td colSpan={5} style={{padding:'10px 12px',fontSize:12,color:'#3B2A21'}}>TOTALS ({filtered.length} jobs)</td>
                  <td style={{padding:'10px 12px',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#556B2F'}}>{fmt(totals.revenue)}</td>
                  <td style={{padding:'10px 12px',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(totals.material_cost)}</td>
                  <td style={{padding:'10px 12px',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(totals.external_cost)}</td>
                  <td style={{padding:'10px 12px',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(totals.total_cost)}</td>
                  <td style={{padding:'10px 12px',fontFamily:'Cormorant Garamond,serif',fontSize:15,color:totals.gross_profit>=0?'#2E6B3A':'#8F2E24'}}>{fmt(totals.gross_profit)}</td>
                  <td style={{padding:'10px 12px',color:parseFloat(avgMargin)>=30?'#2E6B3A':parseFloat(avgMargin)>=15?'#8B5E0A':'#8F2E24'}}>{avgMargin}% avg</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
